import { useCallback, useEffect, useState } from "react";
import type { Oekt } from "../state/session";
import { useLevende } from "../state/levende";
import * as deltakere from "../state/deltakere";

/**
 * Deltakerne i produksjonen.
 *
 * ========  FORKORTELSEN ER DET SOM BETYR NOE  ========
 *
 * ALI for Alina. Det er den regi faktisk skriver i loggen, og den rapporten
 * slår opp for å vise hvem det var. Navnet står ved siden av for at den som
 * fører lista skal vite hvem hen snakker om — men det er forkortelsen som
 * er i bruk, og derfor står den først.
 *
 * Forkortelser er dessuten PSEUDONYMISERING, ikke anonymisering: koblingen
 * finnes her, og innholdet i loggen er ofte identifiserende i seg selv. Se
 * `docs/09-juridisk.md`. Det er en god grunn til å holde lista kort.
 *
 * ========  ROLLEN GIR ETT ELEMENT MER, IKKE EN ANNEN FLATE  ========
 *
 * Alle ser lista — de må, for å kunne lese loggen. Redaktør, prodleder og
 * produsent ser i tillegg feltene som skriver. Samme regel som på
 * `OpplastingScreen`, og over den den ufravikelige: rolle styrer skriving
 * og oppsett, aldri lesing.
 *
 * ========  INGEN SLETTEKNAPP  ========
 *
 * Synkspørsmål og svar peker hit med `on delete cascade`. En deltaker som
 * slettes tar dem med seg, altså loggen fra en dag ingen lenger kan se hva
 * handlet om. «Ikke med lenger» setter en sluttdato, og den kan tas bort
 * igjen.
 */
export function DeltakereScreen({
  oekt, kanal = "deltakere", redigerbar = false,
}: {
  readonly oekt: Oekt;
  /**
   * Om lista kan ENDRES her.
   *
   * ========  BARE UNDER OPPSETT  ========
   *
   * Brukeren 01.11.2026: «Man skal kun kunne legge til ny deltaker fra
   * Innhold-flata under oppsett.» Det er samme deling som for
   * dokumentene og produksjonene: konsollen og oppsettet forvalter,
   * arbeidsflata arbeider. Man staar paa sett naar man leser denne
   * lista, og et skjema der er et sted aa gjoere en feil.
   *
   * Flata er den samme komponenten begge steder — lista skal se lik ut
   * — og det er bare skrivingen som hoerer hjemme ett av dem.
   */
  readonly redigerbar?: boolean;
  /**
   * Navnet paa realtime-kanalen.
   *
   * MAA VAERE ULIKT PER MONTERING. Pageren monterer alle sidene samtidig,
   * og denne komponenten staar to steder: paa sin egen fane og inne i
   * Oppsett · Innhold. To abonnementer med samme navn gjoer at det andre
   * legger til lyttere etter `subscribe()`, og da kaster realtime-klienten
   * — hele flata blir hvit. Se state/levende.ts; det er andre gang den
   * fella slaar til.
   */
  readonly kanal?: string;
}): JSX.Element {
  const [liste, setListe] = useState<readonly deltakere.Deltaker[]>([]);
  const [navn, setNavn] = useState("");
  const [kort, setKort] = useState("");
  const [feil, setFeil] = useState<string | null>(null);
  const skriver = deltakere.kanSkrive(oekt);

  const les = useCallback((): void => {
    void (async () => setListe(await deltakere.hent(oekt.production.id)))();
  }, [oekt.production.id]);

  useEffect(les, [les]);
  /* Egen kanal. Pageren monterer alle sidene samtidig, og to flater med
     samme kanalnavn tar hverandre ned — se state/levende.ts. */
  useLevende(kanal, ["participant"], les);

  const leggTil = useCallback((): void => {
    if (navn.trim() === "" || kort.trim() === "") return;
    void (async () => {
      const svar = await deltakere.leggTil(oekt.production.id, navn, kort);
      if (!svar.ok) { setFeil(svar.feil ?? "Deltakeren ble ikke lagret."); return; }
      setFeil(null);
      setNavn("");
      setKort("");
      les();
    })();
  }, [navn, kort, oekt.production.id, les]);

  const aktive = liste.filter((d) => d.sluttet === null);
  const ute = liste.filter((d) => d.sluttet !== null);

  return (
    <div className="flate">
      <div className="flate__innhold">
        <div className="deltakere">
          <div className="loggkort">
            <div className="loggkort__hode">
              <span className="loggkort__navn">Deltakere</span>
              <span className="loggkort__lag">{aktive.length} med</span>
            </div>

            {aktive.length === 0 && ute.length === 0 ? (
              <p className="tomtekst">
                Ingen deltakere lagt inn ennå.
                {skriver && redigerbar
                  ? " Forkortelsen er den regi skriver i loggen."
                  : " De legges inn under Oppsett · Innhold."}
              </p>
            ) : null}

            {aktive.map((d) => (
              <Rad key={d.id} d={d} skriver={skriver && redigerbar} etterpaa={les} />
            ))}

            {ute.length === 0 ? null : (
              <>
                <div className="deltakere__skille">Ikke med lenger</div>
                {ute.map((d) => (
                  <Rad key={d.id} d={d} skriver={skriver && redigerbar} etterpaa={les} />
                ))}
              </>
            )}
          </div>

          {!skriver || !redigerbar ? null : (
            <div className="loggkort">
              <div className="loggkort__hode">
                <span className="loggkort__navn">Ny deltaker</span>
              </div>
              <form
                className="deltakere__ny"
                onSubmit={(e) => { e.preventDefault(); leggTil(); }}
              >
                <input
                  className="deltakere__felt deltakere__felt--kort mono"
                  value={kort}
                  onChange={(e) => setKort(e.target.value)}
                  placeholder="ALI"
                  aria-label="Forkortelse"
                  maxLength={8}
                />
                <input
                  className="deltakere__felt"
                  value={navn}
                  onChange={(e) => setNavn(e.target.value)}
                  placeholder="Alina Ask"
                  aria-label="Navn"
                />
                <button
                  type="submit"
                  className="synk__knapp synk__knapp--hoved"
                  disabled={navn.trim() === "" || kort.trim() === ""}
                >
                  Legg til
                </button>
              </form>
              {feil === null ? null : <p className="innlogging__feil">{feil}</p>}
              <p className="merknad">
                Forkortelsen skrives i loggen og settes med store bokstaver.
                Hold lista kort — den kobler et navn til alt som står om
                personen.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Rad({
  d, skriver, etterpaa,
}: {
  readonly d: deltakere.Deltaker;
  readonly skriver: boolean;
  etterpaa(): void;
}): JSX.Element {
  const [navn, setNavn] = useState(d.navn);
  const [kort, setKort] = useState(d.forkortelse);

  /* Navnet kan endres av en annen mens du står her. Da skal feltet følge
     etter, ikke stå igjen med din gamle avskrift. */
  useEffect(() => { setNavn(d.navn); setKort(d.forkortelse); }, [d.navn, d.forkortelse]);

  const lagre = useCallback((): void => {
    if (navn === d.navn && kort === d.forkortelse) return;
    void deltakere.endre(d.id, { navn, forkortelse: kort }).then(etterpaa);
  }, [d.id, d.navn, d.forkortelse, navn, kort, etterpaa]);

  return (
    <div className={`deltakere__rad${d.sluttet === null ? "" : " deltakere__rad--ute"}`}>
      {skriver ? (
        <>
          <input
            className="deltakere__felt deltakere__felt--kort mono"
            value={kort}
            onChange={(e) => setKort(e.target.value)}
            onBlur={lagre}
            aria-label={`Forkortelse for ${d.navn}`}
            maxLength={8}
          />
          <input
            className="deltakere__felt"
            value={navn}
            onChange={(e) => setNavn(e.target.value)}
            onBlur={lagre}
            aria-label={`Navn på ${d.forkortelse}`}
          />
          <button
            type="button"
            className="deltakere__ut"
            onClick={() => {
              void deltakere.settSluttet(d.id, d.sluttet === null).then(etterpaa);
            }}
          >
            {d.sluttet === null ? "Ikke med lenger" : "Ta inn igjen"}
          </button>
        </>
      ) : (
        <>
          <span className="deltakere__kort mono">{d.forkortelse}</span>
          <span className="deltakere__navn">{d.navn}</span>
        </>
      )}
    </div>
  );
}
